import {
	MagnifyingGlassIcon,
	ComputerDesktopIcon,
	CpuChipIcon,
	AcademicCapIcon,
	DocumentArrowDownIcon,
	NewspaperIcon,
	InformationCircleIcon,
	XMarkIcon,
	DocumentTextIcon,
	BookOpenIcon,
	SwatchIcon,
} from '@heroicons/react/24/outline'
import { Command } from 'cmdk'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { trackEvent } from '#app/utils/tracking.ts'
import { sanityIcons } from '#app/utils/sanity-icons'

// The generated icon map has literal keys; lookups come from CMS data, so
// treat it as a string-keyed record (missing keys yield undefined).
const sanityIconByKey = sanityIcons as Record<string, string>
import { SanitySvgIcon } from './sanity-svg-icon'

interface CommandPaletteProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

/*
 * Static Tools list for the palette's empty state.
 *
 * These mirror the `tool` documents in Sanity that drive the site's Tools
 * menu. They are duplicated here rather than fetched, so the copy had drifted
 * — the menu said "All things hardware and more" while the palette said
 * "Hardware catalogue & installers", and The Brand Box was missing entirely.
 *
 * Re-aligned to match Sanity. The real fix is to load the tool documents
 * instead of hardcoding them; until then, changing a tool in Studio means
 * changing it here too.
 */
// Mock data with generated Sanity icons
const mockTools = [
	{
		id: 'hardware-center',
		title: 'Hardware Center',
		description: 'All things hardware and more',
		iconKey: 'Hardware Center',
		category: 'Tools',
		url: '/hardware-center',
	},
	{
		id: 'case-collection',
		title: 'Case Collection',
		description: 'Find previous use cases from the market',
		iconKey: 'Case Collection',
		category: 'Tools',
		url: '/case-collection',
	},
	{
		id: 'brand-box',
		title: 'The Brand Box',
		description: 'Stay visually aligned with all things Zeta',
		iconKey: 'Concept Creator',
		category: 'Tools',
		url: '/brand',
	},
]

const mockKnowledgeHub = [
	{
		id: 'fsp-model',
		title: 'FSP Model Overview',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-engage', // Using main FSP engage icon
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp',
	},
	{
		id: 'engage-suite',
		title: 'Engage Suite',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-engage',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/engage-suite',
	},
	{
		id: 'concept-consulting',
		title: 'Concept & Consulting',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-concept',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/concept-and-consulting',
	},
	{
		id: 'creative-content',
		title: 'Creative & Content',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-content',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/creative-and-content',
	},
	{
		id: 'immersive-audio',
		title: 'Immersive Audio',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-immersive',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/immersive-and-audio',
	},
	{
		id: 'hardware-knowledge',
		title: 'Hardware Knowledge',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-hardware',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/hardware',
	},
	{
		id: 'flexible-financing',
		title: 'Flexible Financing',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-financing',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/flexible-financing',
	},
	{
		id: 'project-management',
		title: 'Project Management',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-project',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/project-management',
	},
	{
		id: 'customer-care',
		title: 'Customer Care',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-customer',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/customer-care',
	},
	{
		id: 'analytics-retail-media',
		title: 'Analytics & Retail Media',
		description: 'Full Solution Partner framework',
		iconKey: 'fsp-analytics',
		category: 'Knowledge Hub',
		url: 'knowledge-hub/fsp/analytics-and-retail-media',
	},
]

const mockNavigation = [
	{
		id: 'home',
		title: 'Home',
		description: 'Go to homepage',
		iconKey: null, // Not available in Sanity, will use fallback
		category: 'Navigation',
		url: '/',
	},
	
	{
		id: 'bookmarks',
		title: 'Bookmarks',
		description: 'View saved pages',
		iconKey: 'Bookmarks',
		category: 'Navigation',
		url: '/bookmarks',
	},
	{
		// Was "Glossary" under Tools. It is a reference surface rather than a
		// tool, so it sits at the end with the other navigation destinations.
		id: 'topics',
		title: 'Topics',
		description: 'The terms and jargon explained',
		iconKey: 'Glossary',
		category: 'Navigation',
		url: '/knowledge-hub/topics',
	},
]

interface SearchResult {
	id: string
	title: string
	description?: string
	url: string
	content_type: 'hardware' | 'knowledge' | 'glossary' | 'downloads' | 'hardware-article' | 'news' | 'case-study' | 'fsp' | 'brand'
	type?: string
	manufacturer?: string
	section?: string
	term?: string
	definition?: string
	category?: string
	file_type?: string
	content_type_detail?: string
	access_level?: string
	subtitle?: string
	extract?: string
	sections?: string[]
	tags?: string[]
	featured?: boolean
	category_title?: string
	short_description?: string
	industry?: string
	country?: string
	client?: string
	pillar_title?: string
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
	const [search, setSearch] = useState('')
	const [searchResults, setSearchResults] = useState<SearchResult[]>([])
	const [isSearching, setIsSearching] = useState(false)
	const navigate = useNavigate()
	const inputRef = useRef<HTMLInputElement>(null)

	// Debounced search for all content types.
	//
	// The cleanup must abort the in-flight request, not just clear the timer.
	// Clearing the timer alone stops a *pending* search from starting but does
	// nothing about one already on the wire, so typing "gregg" then "s" left
	// two requests racing and setSearchResults ran for whichever landed last.
	// When the older response won you saw results for the previous keystroke
	// while the box showed the newer term — intermittently, which is what made
	// it look like adding a space changed the results.
	useEffect(() => {
		if (!search || search.length < 2) {
			setSearchResults([])
			return
		}

		const controller = new AbortController()

		const timer = setTimeout(async () => {
			setIsSearching(true)
			try {
				const response = await fetch(
					`/api/search?q=${encodeURIComponent(search)}&limit=8`,
					{ signal: controller.signal },
				)
				if (response.ok) {
					const data = (await response.json()) as any
					const results =
						data.results?.map((r: any) => ({
							id: r.document.id,
							title: r.document.title,
							description: r.document.description,
							url: r.document.url,
							content_type: r.document.content_type,
							type: r.document.type,
							manufacturer: r.document.manufacturer,
							section: r.document.section,
							term: r.document.term,
							definition: r.document.definition,
							category: r.document.category,
							file_type: r.document.file_type,
							content_type_detail: r.document.content_type_detail,
							access_level: r.document.access_level,
							subtitle: r.document.subtitle,
							extract: r.document.extract,
							sections: r.document.sections,
							tags: r.document.tags,
							featured: r.document.featured,
							category_title: r.document.category_title,
							short_description: r.document.short_description,
							industry: r.document.industry,
							country: r.document.country,
							client: r.document.client,
							pillar_title: r.document.pillar_title,
						})) || []
					setSearchResults(results)
					trackEvent('global_search_completed', {
						search_term: search,
						results_count: results.length,
					})
				}
			} catch (error) {
				// An abort is the expected path when the user keeps typing —
				// it is not a failure and must not blank the current results.
				if ((error as Error)?.name === 'AbortError') return
				console.error('Search error:', error)
				setSearchResults([])
			} finally {
				// Same reasoning: an aborted request must not clear the
				// spinner belonging to the request that superseded it.
				if (!controller.signal.aborted) setIsSearching(false)
			}
		}, 300) // 300ms debounce

		return () => {
			clearTimeout(timer)
			controller.abort()
		}
	}, [search])

	// Handle Escape key when modal is open
	useEffect(() => {
		if (!open) {
			// Reset state when modal closes
			setSearch('')
			setSearchResults([])
			return
		}

		const down = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault()
				onOpenChange(false)
			}
		}

		document.addEventListener('keydown', down)
		return () => document.removeEventListener('keydown', down)
	}, [open, onOpenChange])

	// Auto-focus search input when dialog opens
	useEffect(() => {
		if (open && inputRef.current) {
			inputRef.current.focus()
		}
	}, [open])

	const handleResultSelect = (title: string, url: string, resultType: string) => {
		trackEvent('global_search_result_selected', {
			search_term: search,
			result_type: resultType,
			result_name: title,
		})
		void navigate(url)
		onOpenChange(false)
		setSearch('')
	}

	const handleSelect = (value: string) => {
		const allItems = [...mockTools, ...mockKnowledgeHub, ...mockNavigation]
		const item = allItems.find((item) => item.id === value)
		if (item) {
			handleResultSelect(item.title, item.url, item.category)
		}
	}

	const handleClear = () => {
		setSearch('')
		setSearchResults([])
		if (inputRef.current) {
			inputRef.current.focus()
		}
	}

	if (!open) return null

	return (
		<div 
			className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
			onClick={() => onOpenChange(false)}
		>
			<div 
				className="fixed top-1/2 left-1/2 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2"
				onClick={(e) => e.stopPropagation()}
			>
				<Command shouldFilter={false} className="py-4 dark:border-accent bg-background mx-4 overflow-hidden rounded-xl border border-black/20 shadow-2xl [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground  [&_[cmdk-group-heading]]:tracking-wider">
					<div className="flex items-center px-4">
						<MagnifyingGlassIcon className="text-muted-foreground mr-3 h-5 w-5" />
						<Command.Input
							ref={inputRef}
							value={search}
							onValueChange={setSearch}
							placeholder="Search in docs"
							className="placeholder:text-muted-foreground flex-1 bg-transparent py-4 text-base outline-none"
							autoFocus
						/>
						<div className="flex items-center gap-2">
							{search && (
								<button
									onClick={handleClear}
									className="text-muted-foreground hover:text-foreground rounded p-1 text-xs transition-colors"
									aria-label="Clear search"
								>
									Clear
								</button>
							)}
							<button
								onClick={() => onOpenChange(false)}
								className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors cursor-pointer hover:bg-card-hover"
								aria-label="Close search"
							>
								<XMarkIcon className="h-4 w-4" />
							</button>
						</div>
					</div>

					<Command.List className="max-h-96 overflow-y-auto p-2">
						<Command.Empty className="text-muted-foreground flex items-center justify-center py-8">
							{isSearching ? 'Searching...' : `No results found for "${search}"`}
						</Command.Empty>

						{/* Search Results */}
						{searchResults.length > 0 && (
							<>
								{/* Hardware Results */}
								{searchResults.filter(r => r.content_type === 'hardware').length > 0 && (
									<Command.Group  heading="Hardware Catalogue">
										{searchResults.filter(r => r.content_type === 'hardware').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md ">
													{item.type === 'screen' ? (
														<ComputerDesktopIcon className="h-4 w-4" />
													) : (
														<CpuChipIcon className="h-4 w-4" />
													)}
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.description || `${item.manufacturer || ''} ${item.type}`}
													</div>
												</div>
												<div className="text-muted-foreground text-xs capitalize">
													{item.type}
												</div>
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* Knowledge Hub Results */}
								{searchResults.filter(r => r.content_type === 'knowledge').length > 0 && (
									<Command.Group heading="Knowledge Hub">
										{searchResults.filter(r => r.content_type === 'knowledge').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md ">
													<AcademicCapIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.section && `${item.section} • `}{item.description}
													</div>
												</div>
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* FSP Results */}
								{searchResults.filter(r => r.content_type === 'fsp').length > 0 && (
									<Command.Group heading="FSP">
										{searchResults.filter(r => r.content_type === 'fsp').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md ">
													<AcademicCapIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.pillar_title && `${item.pillar_title} • `}{item.description}
													</div>
												</div>
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* Glossary Results — surfaced as "Topics", which is what they read as */}
								{searchResults.filter(r => r.content_type === 'glossary').length > 0 && (
									<Command.Group heading="Topics">
										{searchResults.filter(r => r.content_type === 'glossary').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md ">
													<BookOpenIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.term || item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.category && `${item.category} • `}{item.definition || item.description}
													</div>
												</div>
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* Brand Box — the portal page, not the asset files. Placed
								    above Downloads so the destination reads before the dozens
								    of individual assets that share its wording. */}
								{searchResults.filter(r => r.content_type === 'brand').length > 0 && (
									<Command.Group heading="The Brand Box">
										{searchResults.filter(r => r.content_type === 'brand').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md ">
													<SwatchIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.description}
													</div>
												</div>
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* Downloads Results */}
								{searchResults.filter(r => r.content_type === 'downloads').length > 0 && (
									<Command.Group heading="Downloads">
										{searchResults.filter(r => r.content_type === 'downloads').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md ">
													<DocumentArrowDownIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.file_type && `${item.file_type.toUpperCase()} • `}
														{item.category && `${item.category} • `}
														{item.description}
													</div>
												</div>
												<div className="text-muted-foreground text-xs">
													{item.content_type_detail === 'external' ? 'External' : 'Download'}
												</div>
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* content_type is 'hardware-article' for historical reasons —
								    these are the Articles & Guides at /articles-guides, which is
								    what the heading says. Renaming the content_type would mean a
								    reindex, so only the label changes. */}
								{searchResults.filter(r => r.content_type === 'hardware-article').length > 0 && (
									<Command.Group heading="Articles & Guides">
										{searchResults.filter(r => r.content_type === 'hardware-article').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md">
													<InformationCircleIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.category_title && `${item.category_title} • `}
														{item.subtitle || item.description}
													</div>
												</div>
												{item.featured && (
													<div className="text-muted-foreground text-xs">
														Featured
													</div>
												)}
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* News Results */}
								{searchResults.filter(r => r.content_type === 'news').length > 0 && (
									<Command.Group heading="News">
										{searchResults.filter(r => r.content_type === 'news').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md">
													<NewspaperIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.sections && item.sections.length > 0 && `${item.sections.join(', ')} • `}
														{item.extract || item.description}
													</div>
												</div>
												{item.featured && (
													<div className="text-muted-foreground text-xs">
														Featured
													</div>
												)}
											</Command.Item>
										))}
									</Command.Group>
								)}

								{/* Case Study Results */}
								{searchResults.filter(r => r.content_type === 'case-study').length > 0 && (
									<Command.Group heading="Case Studies">
										{searchResults.filter(r => r.content_type === 'case-study').map((item) => (
											<Command.Item
												key={item.id}
												value={item.id}
												onSelect={() => handleResultSelect(item.title, item.url, item.content_type)}
												className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
											>
												<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md">
													<DocumentTextIcon className="h-4 w-4" />
												</div>
												<div className="min-w-0 flex-1">
													<div className="font-medium">{item.title}</div>
													<div className="text-muted-foreground truncate text-xs">
														{item.industry && `${item.industry} • `}
														{item.country && `${item.country} • `}
														{item.short_description || item.description}
													</div>
												</div>
												{item.featured && (
													<div className="text-muted-foreground text-xs">
														Featured
													</div>
												)}
											</Command.Item>
										))}
									</Command.Group>
								)}
							</>
						)}

						{/* Tools Section - only show when not searching or no search results */}
						{(!search || searchResults.length === 0) && (
						<Command.Group heading="Tools">
							{mockTools.map((tool) => {
								return (
									<Command.Item
										key={tool.id}
										value={tool.id}
										onSelect={() => handleSelect(tool.id)}
										className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
									>
										<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md">
											<SanitySvgIcon
												svgCode={tool.iconKey ? sanityIconByKey[tool.iconKey] : undefined}
												size="sm"
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="font-medium">{tool.title}</div>
											<div className="text-muted-foreground truncate text-xs">
												{tool.description}
											</div>
										</div>
									</Command.Item>
								)
							})}
						</Command.Group>
						)}

						{/* Knowledge Hub Section - only show when not searching or no search results */}
						{(!search || searchResults.length === 0) && (
						<Command.Group heading="Knowledge Hub">
							{mockKnowledgeHub.map((item) => {
								return (
									<Command.Item
										key={item.id}
										value={item.id}
										onSelect={() => handleSelect(item.id)}
										className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
									>
										<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md">
											<SanitySvgIcon
												svgCode={item.iconKey ? sanityIconByKey[item.iconKey] : undefined}
												size="sm"
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="font-medium">{item.title}</div>
											<div className="text-muted-foreground truncate text-xs">
												{item.description}
											</div>
										</div>
										<div className="text-muted-foreground text-xs">
											Knowledge Hub &gt; {item.title}
										</div>
									</Command.Item>
								)
							})}
						</Command.Group>
						)}

						{/* Navigation Section - only show when not searching or no search results */}
						{(!search || searchResults.length === 0) && (
						<Command.Group heading="Navigation">
							{mockNavigation.map((item) => {
								return (
									<Command.Item
										key={item.id}
										value={item.id}
										onSelect={() => handleSelect(item.id)}
										className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm"
									>
										<div className="bg-card flex h-8 w-8 items-center justify-center rounded-md">
											<SanitySvgIcon
												svgCode={item.iconKey ? sanityIconByKey[item.iconKey] : undefined}
												size="sm"
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="font-medium">{item.title}</div>
											<div className="text-muted-foreground truncate text-xs">
												{item.description}
											</div>
										</div>
									</Command.Item>
								)
							})}
						</Command.Group>
						)}
					</Command.List>

			
				</Command>
			</div>
		</div>
	)
}
